import React from "react";
import { observer } from "mobx-react";
import { Progress, Alert } from "antd";

import { CerGenerationStatus, aiTemplateShortName } from "@app/constants";

import "./generation-progress.scoped.scss";

const BAR_COLOR_ACTIVE = "#6d84c2";

// Human labels for the pipeline steps (the `command` on each upstream job). The
// list is the CER pipeline's; a template whose steps we have no labels for falls
// back to a tidied-up command name rather than to nothing.
const STEP_LABELS = {
    "extract-pdfs": "Extracting documents",
    "chunk-docs": "Chunking documents",
    "embed-chunks": "Embedding content",
    "identify-docs": "Identifying documents",
    "identify-studies": "Identifying studies",
    "extract-agent": "Extracting data",
    generate: "Generating report",
};

// "extract-pdfs" -> "Extract pdfs". Not a great label, but it reads as a step
// rather than as a leaked internal name.
const prettifyCommand = (command) => {
    const words = String(command || "")
        .replace(/[-_]+/g, " ")
        .trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : null;
};

const JOB_DONE = [
    CerGenerationStatus.COMPLETED,
    CerGenerationStatus.SKIPPED,
    CerGenerationStatus.FAILED,
];

function currentStepLabel(jobs = []) {
    const job =
        jobs.find((j) => j.status === CerGenerationStatus.RUNNING) ||
        jobs.find((j) => !JOB_DONE.includes(j.status));
    if (!job) {
        return null;
    }
    return STEP_LABELS[job.command] || prettifyCommand(job.command);
}

const GenerationWarnings = observer(({ warnings }) => {
    if (!warnings?.length) {
        return null;
    }

    return (
        <Alert
            className="cerGenerationWarnings"
            type="warning"
            showIcon
            message={
                warnings.length === 1
                    ? "The configuration had 1 warning"
                    : `The configuration had ${warnings.length} warnings`
            }
            description={
                <ul>
                    {warnings.map((warning, i) => (
                        <li key={`${warning.path || ""}-${i}`}>
                            {warning.path ? <b>{warning.path}: </b> : null}
                            {warning.message}
                        </li>
                    ))}
                </ul>
            }
        />
    );
});

GenerationWarnings.displayName = "GenerationWarnings";

const GenerationProgress = observer(({ reportDocument }) => {
    const generation = reportDocument?.cerGeneration;

    // Only relevant for AI-generated documents.
    if (!reportDocument?.cerRunId || !generation) {
        return null;
    }

    const { status, progress, jobs, warnings, errorMessage } = generation;
    const shortName = aiTemplateShortName(reportDocument.aiTemplate);

    if (status === CerGenerationStatus.FAILED && errorMessage) {
        return (
            <>
                <Alert
                    className="cerGenerationBanner"
                    type="error"
                    showIcon
                    message={`${shortName} generation failed`}
                    description={errorMessage || `The ${shortName} generation run failed.`}
                />
                <GenerationWarnings warnings={warnings} />
            </>
        );
    }

    if (status !== CerGenerationStatus.QUEUED && status !== CerGenerationStatus.RUNNING) {
        return <GenerationWarnings warnings={warnings} />;
    }

    const percent = Math.round((progress || 0) * 100);
    const stepLabel = currentStepLabel(jobs || []);

    return (
        <>
            <div className="cerGenerationBanner">
                <span className="label">
                    Generating {shortName} report&hellip;
                    {stepLabel && <span className="step"> {stepLabel}</span>}
                </span>
                <Progress
                    className="bar"
                    percent={percent}
                    status="active"
                    size="small"
                    strokeColor={BAR_COLOR_ACTIVE}
                />
            </div>
            <GenerationWarnings warnings={warnings} />
        </>
    );
});

GenerationProgress.displayName = "GenerationProgress";

export default GenerationProgress;
